import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, IconButton, Chip, CircularProgress, Alert,
  Menu as MuiMenu, useTheme, alpha,
} from '@mui/material';
import {
  Add as AddIcon, LocalBar as MenuListIcon,
  Edit as EditIcon, Archive as ArchiveIcon, ArrowDropDown as DropdownIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import menuService from '../../../../services/menuService';
import type { Menu as MenuDoc, MenuStatus, MenuType } from '../../../../types/menu';
import { menuTypeLabel } from './menuTypeLabels';

const STATUS_COLORS: Record<MenuStatus, 'default' | 'primary' | 'success' | 'warning'> = {
  DRAFT: 'default',
  ACTIVE: 'success',
  ARCHIVED: 'warning',
};

const MENU_TYPES: MenuType[] = [
  'COCKTAIL', 'FULL', 'BY_THE_GLASS', 'RESERVE', 'EVENT', 'PRIVATE',
  'FOOD', 'BEER', 'SPIRITS', 'TASTING', 'RETAIL', 'MIXED', 'OTHER',
];
const MENU_STATUSES: MenuStatus[] = ['DRAFT', 'ACTIVE', 'ARCHIVED'];

const thStyle = {
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase' as const,
};

const MenuList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { venueId } = useVenue();
  const base = `/admin/venues/${venueId}/menus`;

  const [items, setItems] = useState<MenuDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [typeFilter, setTypeFilter] = useState<MenuType | ''>('');
  const [statusFilter, setStatusFilter] = useState<MenuStatus | 'all' | ''>('');
  const [pagination, setPagination] = useState({ page: 0, pageSize: 25, totalItems: 0 });

  const fetchItems = useCallback(
    async (type: MenuType | '', status: MenuStatus | 'all' | '', page: number, pageSize: number) => {
      setLoading(true);
      setFetchError('');
      try {
        const result = await menuService.getMenus(
          venueId,
          page + 1,
          pageSize,
          type || undefined,
          status || undefined,
        );
        setItems(result.data);
        setPagination(prev => ({ ...prev, page, pageSize, totalItems: result.pagination.totalItems }));
      } catch (err) {
        console.error('Failed to load menus', err);
        setFetchError('Could not load menus. Check your connection and try again.');
      } finally {
        setLoading(false);
      }
    },
    [venueId],
  );

  useEffect(() => {
    fetchItems(typeFilter, statusFilter, pagination.page, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, statusFilter, pagination.page, pagination.pageSize]);

  const handleArchive = async (e: React.MouseEvent, menu: MenuDoc) => {
    e.stopPropagation();
    if (!window.confirm(`Archive menu "${menu.name}"? Its items will be preserved.`)) return;
    try {
      await menuService.deleteMenu(venueId, menu._id);
      fetchItems(typeFilter, statusFilter, pagination.page, pagination.pageSize);
    } catch (err) {
      console.error('Failed to archive menu', err);
      setFetchError(`Could not archive "${menu.name}". Please try again.`);
    }
  };

  // ── Status chip menu ────────────────────────────────────────────────────────
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuTarget, setMenuTarget] = useState<MenuDoc | null>(null);
  const [statusChanging, setStatusChanging] = useState<string | null>(null);

  const openStatusMenu = (e: React.MouseEvent<HTMLElement>, menu: MenuDoc) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuTarget(menu);
  };

  const closeStatusMenu = () => {
    setMenuAnchor(null);
    setMenuTarget(null);
  };

  const handleStatusChange = async (newStatus: MenuStatus) => {
    if (!menuTarget) return;
    const target = menuTarget;
    closeStatusMenu();
    setStatusChanging(target._id);
    try {
      await menuService.updateMenu(venueId, target._id, { status: newStatus });
      fetchItems(typeFilter, statusFilter, pagination.page, pagination.pageSize);
    } catch (err) {
      console.error('Failed to update status', err);
      setFetchError(`Could not update status for "${target.name}". Please try again.`);
    } finally {
      setStatusChanging(null);
    }
  };

  const hasActiveFilters = Boolean(typeFilter || statusFilter);

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        {/* Header */}
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <MenuListIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" fontWeight={600}>Menus</Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`${base}/create`)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            New Menu
          </Button>
        </Box>

        {/* Subtitle / scope advisory — Phase 4.H */}
        <Box sx={{ px: 3, pt: 2 }}>
          <Alert severity="info" sx={{ borderRadius: 1 }}>
            Menus can be wine lists, cocktail lists, food menus or mixed menus. Wine lists are now managed here as a menu type.
          </Alert>
        </Box>

        {/* Fetch error */}
        {fetchError && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="error" onClose={() => setFetchError('')} sx={{ borderRadius: 1 }}>{fetchError}</Alert>
          </Box>
        )}

        {/* Filters */}
        <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, flexWrap: 'wrap', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              label="Type"
              onChange={e => {
                setTypeFilter(e.target.value as MenuType | '');
                setPagination(p => ({ ...p, page: 0 }));
              }}
            >
              <MenuItem value="">All types</MenuItem>
              {MENU_TYPES.map(t => <MenuItem key={t} value={t}>{menuTypeLabel(t)}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={e => {
                setStatusFilter(e.target.value as MenuStatus | 'all' | '');
                setPagination(p => ({ ...p, page: 0 }));
              }}
            >
              <MenuItem value="">Active &amp; Draft</MenuItem>
              {MENU_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              <MenuItem value="all">All statuses</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Table */}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Name</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Type</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Status</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Description</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Updated</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={32} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box sx={{ py: 7, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <MenuListIcon sx={{ fontSize: 44, mb: 1.5, opacity: 0.25 }} />
                      <Typography variant="body2" fontWeight={500} gutterBottom>
                        {hasActiveFilters ? 'No menus match the current filters.' : 'No menus yet.'}
                      </Typography>
                      {!hasActiveFilters && (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 420, mx: 'auto', mb: 1 }}>
                            Create a wine list, cocktail menu or mixed menu.
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => navigate(`${base}/create`)}
                            sx={{ mt: 1, textTransform: 'none' }}
                          >
                            Create first menu
                          </Button>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                items.map(menu => (
                  <TableRow
                    key={menu._id}
                    hover
                    onClick={() => navigate(`${base}/${menu._id}`)}
                    sx={{ cursor: 'pointer', opacity: menu.status === 'ARCHIVED' ? 0.55 : 1 }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{menu.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {menuTypeLabel(menu.type)}
                      </Typography>
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      {menu.status === 'ARCHIVED' ? (
                        <Chip label="ARCHIVED" size="small" color="warning" sx={{ fontSize: '0.7rem', height: 22 }} />
                      ) : statusChanging === menu._id ? (
                        <CircularProgress size={16} sx={{ ml: 0.5 }} />
                      ) : (
                        <Chip
                          label={menu.status}
                          size="small"
                          color={STATUS_COLORS[menu.status]}
                          onClick={e => openStatusMenu(e, menu)}
                          onDelete={e => openStatusMenu(e, menu)}
                          deleteIcon={<DropdownIcon />}
                          sx={{ fontSize: '0.7rem', height: 22, cursor: 'pointer' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {menu.description || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {menu.updatedAt ? new Date(menu.updatedAt).toLocaleDateString() : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      <IconButton size="small" onClick={() => navigate(`${base}/edit/${menu._id}`)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {menu.status !== 'ARCHIVED' && (
                        <IconButton size="small" color="warning" onClick={e => handleArchive(e, menu)}>
                          <ArchiveIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={pagination.totalItems}
          page={pagination.page}
          rowsPerPage={pagination.pageSize}
          onPageChange={(_e, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
          onRowsPerPageChange={e => setPagination(prev => ({ ...prev, pageSize: parseInt(e.target.value, 10), page: 0 }))}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      {/* Status change menu */}
      <MuiMenu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeStatusMenu}
        slotProps={{ paper: { elevation: 3, sx: { minWidth: 140, borderRadius: 1.5 } } }}
      >
        {(['DRAFT', 'ACTIVE'] as MenuStatus[])
          .filter(s => s !== menuTarget?.status)
          .map(s => (
            <MenuItem key={s} onClick={() => handleStatusChange(s)} sx={{ fontSize: '0.85rem', gap: 1.5 }}>
              <Chip label={s} size="small" color={STATUS_COLORS[s]} sx={{ fontSize: '0.7rem', height: 20, pointerEvents: 'none' }} />
              Set {s === 'ACTIVE' ? 'Active' : 'Draft'}
            </MenuItem>
          ))
        }
      </MuiMenu>
    </Box>
  );
};

export default MenuList;
